import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Heading,
    SimpleGrid,
    Text,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {useBrand} from '../../contexts/brand-context'
import UgcTile from '../../components/ugc-tile'
import SortSheet from '../../components/sort-sheet'
import {useUgcMedia, UGC_SORT_OPTIONS} from '../../hooks/use-ugc-media'
import GridViewSelector, {GRID_VIEW} from '../product-list/partials/grid-view-selector'
import Sort from '../product-list/partials/sort'
import ItemsCounter from '../product-list/partials/items-counter'
import PageNotFound from '../page-not-found'
import {CAT_MENU_DEFAULT_NAV_SSR_DEPTH, UGC_CATEGORY_LOOKUP_DEPTH} from '../../constants'
import {getRootCategoryId} from '../../utils/brandUtils'
import {findUgcCategory, isUgcPlacementEnabled, isUgcTotalCountEnabled} from '../../utils/ugc-flags'

// UGC social hashtags shown in the landing subtitle, keyed by brand id.
// No backend/env source exists for these yet, so they're hardcoded here to
// keep the page self-contained — update once BV/CMB provides real campaign tags.
const BRAND_UGC_HASHTAG = {
    garage: 'StyledByGarage',
    dynamite: 'StyledByDynamite'
}
const DEFAULT_UGC_HASHTAG = 'StyledByUs'

// Grid view shared across the whole PLP family (category/search + UGC), not
// UGC-specific. Same cookie name, same default-first-then-hydrate pattern,
// and same column values as product-list/use-product-list.js — so a user's
// grid preference set on PLP applies here too, and vice versa.
const GRID_VIEW_COOKIE = 'plpGridView'
const readSavedGridView = () => {
    if (typeof window === 'undefined') return null
    const match = document.cookie.match(/(?:^|;\s*)plpGridView=([^;]+)/)
    // Same legacy localStorage fallback as PLP, for parity.
    return (match ? decodeURIComponent(match[1]) : null) || localStorage.getItem(GRID_VIEW_COOKIE)
}

/**
 * UGC sort control (mobile). The trigger reads "Sort" — not the selected option —
 * and opens a bottom sheet listing the options with a check on the current one,
 * matching the mobile PLP sort. A native <select> was rejected earlier because it
 * is always as wide as its longest option.
 */
const UgcSort = ({sort, setSort, options, ariaLabel}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()

    return (
        <>
            <Button
                variant="filter"
                fontSize={{base: '14px', lg: '16px'}}
                aria-label={ariaLabel}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                onClick={onOpen}
                data-testid="ugc-sort-trigger"
            >
                {ariaLabel}
            </Button>
            <SortSheet
                isOpen={isOpen}
                onClose={onClose}
                options={options}
                selected={sort}
                onSelect={setSort}
                title={ariaLabel}
            />
        </>
    )
}

UgcSort.propTypes = {
    sort: PropTypes.string,
    setSort: PropTypes.func,
    options: PropTypes.array,
    ariaLabel: PropTypes.string
}

const UgcLanding = () => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('UgcLandingPage', {})
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {brandId} = useBrand()
    const location = useLocation()
    const cgid = new URLSearchParams(location.search).get('cgid') || undefined
    const brandHashtag = BRAND_UGC_HASHTAG[brandId] || DEFAULT_UGC_HASHTAG
    const isLandingEnabled = isUgcPlacementEnabled(appConfig, 'landing', site?.id, brandId)
    const isTotalCountEnabled = isUgcTotalCountEnabled(appConfig, brandId)

    // Landing title/description come from the brand's UGC category in BM
    // (pageTitle/pageDescription, flagged `c_isUGC`). The nav's cached tree is
    // L1-only, so when the UGC category sits at a subcategory level it is not in
    // it — fetch a deeper tree for that case before falling back to the inline
    // text. The deeper query is skipped whenever the (free) nav tree already
    // resolves a UGC category.
    const rootCategoryId = getRootCategoryId(site?.id, brandId)
    const {data: navCategoriesTree, isLoading: isNavTreeLoading} = useCategory({
        parameters: {id: rootCategoryId, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
        enabled: isLandingEnabled && Boolean(rootCategoryId)
    })
    const navUgcCategory = useMemo(() => findUgcCategory(navCategoriesTree), [navCategoriesTree])
    const shouldLookDeeper =
        isLandingEnabled && Boolean(rootCategoryId) && !isNavTreeLoading && !navUgcCategory
    const {data: deepCategoriesTree} = useCategory({
        parameters: {id: rootCategoryId, levels: UGC_CATEGORY_LOOKUP_DEPTH},
        enabled: shouldLookDeeper
    })
    const ugcCategory = useMemo(
        () => navUgcCategory || findUgcCategory(deepCategoriesTree),
        [navUgcCategory, deepCategoriesTree]
    )

    // Clicking the UGC nav link while already on the UGC page is a no-op for
    // react-router (same route), so force a full reload to reset the
    // pagination/cursor state and show the first page fresh.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined
        const currentPath = location.pathname.replace(/\/+$/, '')
        if (!currentPath.endsWith('/ugc')) return undefined

        const onClick = (event) => {
            const anchor = event.target?.closest?.('a[href]')
            if (!anchor) return
            const href = anchor.getAttribute('href')
            if (!href) return
            // Match the current UGC path, ignoring query/hash and trailing slash.
            const targetPath = href.split(/[?#]/)[0].replace(/\/+$/, '')
            if (targetPath === currentPath) {
                event.preventDefault()
                window.location.reload()
            }
        }

        document.addEventListener('click', onClick)
        return () => document.removeEventListener('click', onClick)
    }, [location.pathname])

    const [gridView, setGridView] = useState(GRID_VIEW.DEFAULT)
    const isLargeGridView = gridView === GRID_VIEW.ALTERNATIVE

    // Start at DEFAULT (matches SSR); apply the saved cookie/localStorage choice
    // after hydration to avoid a server/client mismatch that would force a re-render.
    useEffect(() => {
        const saved = readSavedGridView()
        if (saved && saved !== gridView) setGridView(saved)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setGridViewPersisted = (view) => {
        // Same cookie PLP writes to — setting it here updates PLP's preference too.
        document.cookie = `${GRID_VIEW_COOKIE}=${view}; path=/; max-age=31536000; samesite=lax`
        setGridView(view)
    }

    const {
        items,
        resultsCount,
        sort,
        setSort,
        isLoading,
        isLoadingMore,
        error,
        hasMore,
        hasPrevious,
        showMore,
        showPrevious
    } = useUgcMedia({placement: 'landing', brandId})

    const sortLabel = intl.formatMessage({
        id: 'ugc_landing.label.sort',
        defaultMessage: 'Sort'
    })

    const sortOptions = useMemo(
        () => [
            {
                value: UGC_SORT_OPTIONS.NEWEST,
                label: intl.formatMessage({
                    id: 'ugc_landing.sort.newest',
                    defaultMessage: 'Newly Added'
                })
            },
            {
                value: UGC_SORT_OPTIONS.POPULAR,
                label: intl.formatMessage({
                    id: 'ugc_landing.sort.popular',
                    defaultMessage: 'Most Popular'
                })
            }
        ],
        [intl]
    )

    if (!isLandingEnabled) {
        return <PageNotFound />
    }

    return (
        <Box {...styles.container} data-testid="ugc-landing-page">
            <Heading as="h1" {...styles.title}>
                {ugcCategory?.pageTitle ||
                    intl.formatMessage({
                        id: 'ugc_landing.title',
                        defaultMessage: 'Styled By You'
                    })}
            </Heading>
            <Text {...styles.subtitle}>
                {ugcCategory?.pageDescription ||
                    intl.formatMessage(
                        {
                            id: 'ugc_landing.subtitle',
                            defaultMessage:
                                'Share your looks on social media by mentioning @{brand} and #{hashtag}.'
                        },
                        {brand: brandId, hashtag: brandHashtag}
                    )}
            </Text>

            <Box {...styles.toolbar}>
                <Box {...styles.toolbarLeft}>
                    {isTotalCountEnabled && !isLoading && (
                        <Text {...styles.itemCount} data-testid="ugc-item-count">
                            {intl.formatMessage(
                                {
                                    id: 'ugc_landing.item_count',
                                    defaultMessage: '{count, plural, one {# item} other {# items}}'
                                },
                                {count: resultsCount}
                            )}
                        </Text>
                    )}
                </Box>

                <Box {...styles.toolbarCenter}>
                    {/* Same split as FiltersHeader: two GridViewSelector instances
                        gated by responsive display, rather than one instance with
                        a responsive prop, for 1:1 structural parity with PLP. */}
                    <Box display={{base: 'none', md: 'block'}}>
                        <GridViewSelector gridView={gridView} setGridView={setGridViewPersisted} />
                    </Box>
                    <Box display={{base: 'block', md: 'none'}}>
                        <GridViewSelector gridView={gridView} setGridView={setGridViewPersisted} />
                    </Box>
                </Box>

                <Box {...styles.toolbarRight}>
                    <Box display={{base: 'none', lg: 'block'}}>
                        <Sort
                            sortingOptions={sortOptions.map(({value, label}) => ({
                                id: value,
                                label
                            }))}
                            sortUrls={sortOptions.map(() => '#')}
                            selectedSortingOption={sort}
                            onSelect={(option) => setSort(option.id)}
                        />
                    </Box>
                    <Box display={{base: 'block', lg: 'none'}}>
                        <UgcSort
                            sort={sort}
                            setSort={setSort}
                            options={sortOptions}
                            ariaLabel={sortLabel}
                        />
                    </Box>
                </Box>
            </Box>

            {hasPrevious && (
                <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap={8}
                    {...styles.showPreviousContainer}
                >
                    <Button
                        variant="webPrimary"
                        isLoading={isLoadingMore}
                        data-testid="ugc-show-previous"
                        onClick={showPrevious}
                        {...styles.showPreviousButton}
                    >
                        {intl.formatMessage({
                            id: 'ugc_landing.button.show_previous',
                            defaultMessage: 'Show Previous'
                        })}
                    </Button>
                </Flex>
            )}

            {error && !isLoading && items.length === 0 && (
                <Text {...styles.errorMessage} data-testid="ugc-error-message">
                    {intl.formatMessage({
                        id: 'ugc_landing.error',
                        defaultMessage: "We couldn't load looks right now. Please try again later."
                    })}
                </Text>
            )}

            <SimpleGrid
                {...styles.grid}
                role="list"
                columns={isLargeGridView ? [3, 3, 6, 6] : [2, 2, 4, 4]}
            >
                {items.map((item) => (
                    <Box key={item.id} role="listitem">
                        <UgcTile item={item} isLargeGridView={isLargeGridView} cgid={cgid} />
                    </Box>
                ))}
            </SimpleGrid>

            {hasMore && (
                <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap={8}
                    {...styles.showMoreContainer}
                >
                    {isTotalCountEnabled && (
                        <ItemsCounter loadedCount={items.length} totalCount={resultsCount} />
                    )}
                    <Button
                        variant="webPrimary"
                        isLoading={isLoadingMore}
                        data-testid="ugc-show-more"
                        onClick={showMore}
                        {...styles.showMoreButton}
                    >
                        {intl.formatMessage({
                            id: 'ugc_landing.button.show_more',
                            defaultMessage: 'Show More'
                        })}
                    </Button>
                </Flex>
            )}
        </Box>
    )
}

export default UgcLanding